import { LitElement, html } from 'lit';
import { updateMacroStepEvent, updateMicroStepEvent, displayProgressBarEvent } from '@cof/refi-web-sdk/dist/utils/update-progress-bar';
import { RefiAnalytics } from '@cof/refi-web-sdk';
import { TagKey } from '@cof/tag-catalog-fs-lienholder-mfe';
import { SessionService } from './core/services/session/session.service.js';
import { DebugService } from './shared/services/debug.service.js';
import { AppService } from './app.service.js';
import { LenderService } from './services/lender.service.js';
import { REFI_LIENHOLDER_CONST } from './shared/constants/app.constants.js';
import { stratum } from '../constants/stratum.js';
import { environment } from '../environment/environement.js';
import './components/lender-checkout/lender-checkout.component.js';
import { ExperimentService } from './shared/services/experiment.service.js';
import { CookieService } from './shared/services/cookie.service.js';

class RefiLienholderApp extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiLienholderApp');
    this.isTesting = false;
    this.checkout = false;
    if (document?.location) {
      this.isTesting = new URL(document.location)?.searchParams?.get('testing') === 'lighthouse';
    }
    if (this.isTesting && !environment.isProdEnv) {
      this.session = SessionService.mockSession;
    } else {
      this.session = SessionService.session || {};
    }
    const appId = this.session?.currentApp?.appId || '';
    stratum.setExternalVar('applicationId', appId);
    this.updateStratumMarketingChannel();
    this.loadingApp = false;
    this.baseHref = document.querySelector('base').getAttribute('href');
    displayProgressBarEvent(true);
    updateMacroStepEvent('Lock in payment', ['Offers', 'Review', 'Contract']);
    updateMicroStepEvent('Contract');
    setTimeout(() => {
      updateMacroStepEvent('Verify information', ['Lienholder', 'Stips']);
    }, 30);
    this.addEventListener(REFI_LIENHOLDER_CONST.ROUTE_CHANGE, () => {
      this.requestUpdate();
    });
    this.analytics = new RefiAnalytics('lienholder', ['sitecatalyst', 'potomac'], appId);
    this.existingExperimentIds = [];
  }

  static get properties() {
    return {
      loadingApp: { type: Boolean },
      experiments: {
        type: Object,
        hasChanged: (newVal, oldVal) => {
          if (newVal !== oldVal) {
            // loop through this.experiments and add the object name to experimentIds array if it's eligible
            const experimentIds = [];
            Object.keys(newVal).forEach(experiment => {
              if (newVal[experiment].isEligible) {
                experimentIds.push(newVal[experiment].allocation);
              } else {
                experimentIds.push(`${experiment}-ineligible`);
              }
            });
            ExperimentService.updateExperiments(experimentIds, this.existingExperimentIds);
            this.existingExperimentIds = experimentIds;
            // Uncomment below in the event that a feature is created for LH
            // FeatureToggleService.updateIsIconicAndToggledOn();
            return true;
          }
          return false;
        },
      },
      checkout: { type: String, attribute: 'checkout-page' },
    };
  }

  createRenderRoot() {
    return this;
  }

  render() {
    this.debug.log('rendered');

    if (this.checkout || (!environment.isProdEnv && this.isCheckoutHash())) {
      return html`
        <div role="main">
          <refi-lender-checkout></refi-lender-checkout>
        </div>
      `;
    }

    return html`
      <div role="main">
        ${window.location.href.endsWith('lender-info')
          ? html`<lit-route
              component="refi-lender-info"
              .resolve="${() =>
                this.resolvePage({
                  requiredState: 'lender-info',
                  import: () => import('./components/lender-info/lender-info.js'),
                }).catch(() => this.debug.error('Unable to resolve lender-info'))}"
            ></lit-route>`
          : ''}
      </div>
      ${this.loadingApp ? html` <refi-loading-spinner message="Loading Lender Info Page" class="_gb-show"></refi-loading-spinner> ` : ''}
    `;
  }

  async firstUpdated() {
    this.loadingApp = true;
    if (this.isCheckoutPage()) {
      this.loadingApp = false;
      LenderService.appId = this.session.eAppId;
    } else {
      await this.updateComplete;
      AppService.updateRoute();
      LenderService.appId = this.session.eAppId;
      stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_LIENHOLDER);
      if (this.session.currentApp?.lenderInfo) {
        LenderService.lienholderModel = this.session.currentApp?.lenderInfo;
      }
      if (this.session.eAppId) {
        this.loadingApp = false;
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  checkState() {
    return Promise.resolve();
  }

  updateStratumMarketingChannel() {
    const parsedCookie = CookieService.getCookie(REFI_LIENHOLDER_CONST.MARKETING_ATTRIBUTE_COOKIE)?.split(/\^|\|/);
    const externalIdFromCookie = parsedCookie?.[1]; // oldest non-bau attribution
    if (parsedCookie?.[0] === 'external_id') {
      // sets var to query param external id if first time with offer code, otherwise cookie
      stratum.setExternalVar('marketingChannel', externalIdFromCookie ?? SessionService.getExternalId());
    }
  }

  async resolvePage(options) {
    try {
      await this.checkState(options.requiredState);
      await options.import();
    } catch (error) {
      this.debug.error('Failed to resolve page', error);
    }
  }

  isCheckoutPage() {
    return this.checkout || (!environment.isProdEnv && this.isCheckoutHash());
  }

  isCheckoutHash() {
    return window.location.hash && window.location.hash.includes('#/nonprod/lienholder/checkout');
  }
}

customElements.define('refi-lienholder-app', RefiLienholderApp);
